import  { useState, useEffect, useMemo } from 'react'
import { SidebarProvider } from "@/components/ui/sidebar";
import StudentHeader from "./StudentHeader";
import StudentSidebar from "./StudentSidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { format, startOfMonth, endOfMonth, eachDayOfInterval } from "date-fns";
import { 
  User, 
  Calendar, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  TrendingUp,
  Award,
  Users,
  Target,
  Flame,
  Loader2
} from "lucide-react";
import { XAxis, YAxis, ResponsiveContainer, Tooltip, BarChart, Bar } from "recharts";
import { db, auth } from "../firebase/config";
import { collection, query, where, getDocs } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

// Types
interface Affiliation { role: string; schoolID?: string; }
interface CustomClaims { affiliations?: Affiliation[]; defaultAffiliation?: Affiliation; schoolID?: string; }
interface StudentData { firstName: string; middleName?: string; lastName: string; registrationNumber: string; class: string; dormName: string; email: string; studentId: string; classId: string; dormId?: string; }
interface AttendanceRecord { id: string; studentId: string; date: string; status: 'present' | 'absent'; reason?: string; markedBy?: string; markedAt?: string; classId?: string; }
interface DistributionBucket { range: string; count: number; color: string; }

const StudentAttendanceView = () => {
  // Filters
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));

  // Auth / identity
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [hasValidClaims, setHasValidClaims] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  // Student profile
  const [studentData, setStudentData] = useState<StudentData | null>(null);
  const [dormName, setDormName] = useState<string | null>(null);

  // Attendance data
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [loadingAttendance, setLoadingAttendance] = useState(true);

  // Class distribution
  const [distribution, setDistribution] = useState<DistributionBucket[]>([]);
  const [classAverage, setClassAverage] = useState<number>(0);
  const [loadingClassStats, setLoadingClassStats] = useState(true);


  // Auth & Claims
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async user => {
      setIsAuthLoading(true);
      console.log('[StudentAttendance] Auth state changed:', user?.email);
      if (!user) { 
        setIsAuthLoading(false);
        return;
      }

      try {
        const idTokenResult = await user.getIdTokenResult();
        const claims = idTokenResult.claims as CustomClaims;
        const sid = claims.schoolID || claims.defaultAffiliation?.schoolID || claims.affiliations?.[0]?.schoolID;

        if (sid) {
          setSchoolID(sid);
          setHasValidClaims(true);

          // Fetch Student Data
          const studentsRef = collection(db, 'schools', sid, 'students');
          const q = query(studentsRef, where('email', '==', user.email));
          const snap = await getDocs(q);
          if (!snap.empty) {
            const sData = { ...snap.docs[0].data(), studentId: snap.docs[0].id } as any;
            // Map fields to match interface
            const mappedData: StudentData = {
              firstName: sData.firstName,
              middleName: sData.middleName,
              lastName: sData.lastName,
              registrationNumber: sData.registrationNumber,
              class: sData.className || sData.class || '',
              dormName: sData.dormitory || '',
              email: sData.email,
              studentId: sData.studentId,
              classId: sData.currentClassUid || '',
              dormId: sData.dormId || null,
            };
            setStudentData(mappedData);
            
            // Set dorm name directly from dormitory field if available
            setDormName(sData.dormitory || null);
          }
        }
      } catch (e) {
        console.error("Error fetching auth data", e);
      } finally {
        setIsAuthLoading(false);
      }
    });
    return () => unsub();
  }, []);

  // Fetch Student Attendance
  useEffect(() => {
    if (!schoolID || !studentData) return;
    
    const fetchAttendance = async () => {
      setLoadingAttendance(true);
      try {
        const [y, m] = selectedMonth.split('-').map(Number);
        const start = startOfMonth(new Date(y, m - 1));
        const end = endOfMonth(new Date(y, m - 1));
        const allDays = eachDayOfInterval({ start, end });
        const days = allDays.slice(-50); // Limit to last 50 days to reduce Firebase charges
        
        const records: AttendanceRecord[] = [];
        await Promise.all(days.map(async (day) => {
           const dateStr = format(day, 'yyyy-MM-dd');
           const col = collection(db, 'schools', schoolID, 'attendance', dateStr, 'records');
           const q = query(col, where('studentId', '==', studentData.studentId));
           const snap = await getDocs(q);
           snap.forEach(d => records.push({ id: d.id, ...d.data() } as AttendanceRecord));
        }));
        setAttendanceRecords(records);
      } catch (e) {
        console.error("Error fetching attendance", e);
      } finally {
        setLoadingAttendance(false);
      }
    };
    fetchAttendance();
  }, [schoolID, studentData, selectedMonth]);

  // Fetch Class Stats
  useEffect(() => {
    if (!schoolID || !hasValidClaims || !studentData?.classId) {
      setLoadingClassStats(false);
      return;
    }
    const run = async () => {
      setLoadingClassStats(true);
      try {
        // Get all attendance records for this class for the month
        const [y, m] = selectedMonth.split('-').map(Number);
        const start = startOfMonth(new Date(y, m - 1));
        const end = endOfMonth(new Date(y, m - 1));
        const allDays = eachDayOfInterval({ start, end });
        const days = allDays.slice(-120); // Limit to last 120 days for class average calculation
        let allRecords: AttendanceRecord[] = [];
        
        // Optimization: Use Promise.all
        await Promise.all(days.map(async (day) => {
          const dateStr = format(day, 'yyyy-MM-dd');
          const col = collection(db, 'schools', schoolID, 'attendance', dateStr, 'records');
          const q = query(col, where('classId', '==', studentData.classId));
          const snap = await getDocs(q);
          snap.forEach(docSnap => {
            allRecords.push({ id: docSnap.id, ...(docSnap.data() as Omit<AttendanceRecord, 'id'>) });
          });
        }));

        // Group by studentId
        const byStudent: { [sid: string]: AttendanceRecord[] } = {};
        allRecords.forEach(rec => {
          if (!byStudent[rec.studentId]) byStudent[rec.studentId] = [];
          byStudent[rec.studentId].push(rec);
        });
        
        // Calculate attendance % for each student
        const percentages: number[] = Object.entries(byStudent).map(([_, records]) => {
          const total = records.length;
          const present = records.filter(r => r.status === 'present').length;
          const pct = total ? (present / total) * 100 : null;
          return pct;
        }).filter(p => p !== null) as number[];
        
        // If there are students with records, average their percentages; else, 0
        const avg = percentages.length > 0 ? Math.round(percentages.reduce((a, b) => a + b, 0) / percentages.length) : 0;
        setClassAverage(avg);
        const distData = [
          { range: '90-100%', count: percentages.filter(p => p >= 90).length, color: '#22c55e' },
          { range: '80-89%', count: percentages.filter(p => p >= 80 && p < 90).length, color: '#3b82f6' },
          { range: '70-79%', count: percentages.filter(p => p >= 70 && p < 80).length, color: '#f59e0b' },
          { range: '60-69%', count: percentages.filter(p => p >= 60 && p < 70).length, color: '#ef4444' },
          { range: '<60%', count: percentages.filter(p => p < 60).length, color: '#dc2626' }
        ];
        setDistribution(distData);
      } catch (e) {
        console.error('[StudentAttendance] Failed to fetch class stats:', e);
      } finally {
        setLoadingClassStats(false);
      }
    };
    run();
  }, [schoolID, hasValidClaims, studentData, selectedMonth]);

  const { currentStreak, maxStreak } = useMemo(() => {
    if (!attendanceRecords.length) {
      return { currentStreak: 0, maxStreak: 0 };
    }
    const sorted = [...attendanceRecords].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    let current = 0;
    for (const rec of sorted) {
      if (rec.status === 'present') {
        current++;
      } else {
        break;
      }
    }
    let max = 0;
    let temp = 0;
    for (const rec of sorted) {
      if (rec.status === 'present') {
        temp++;
        max = Math.max(max, temp);
      } else {
        temp = 0;
      }
    }
    return { currentStreak: current, maxStreak: max };
  }, [attendanceRecords]);

  const attendanceStats = useMemo(() => {
    const totalDays = attendanceRecords.length;
    const presentDays = attendanceRecords.filter(r => r.status === 'present').length;
    const absentDays = totalDays - presentDays;
    const attendancePercentage = totalDays > 0 ? Math.round((presentDays / totalDays) * 100) : 0;
    return { totalDays, presentDays, absentDays, attendancePercentage };
  }, [attendanceRecords]);

  const getAttendanceColor = (percentage: number) => {
    if (percentage >= 90) return 'text-green-600';
    if (percentage >= 75) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getFullName = (student: StudentData) => {
    const parts = [student.firstName, student.middleName, student.lastName].filter(Boolean);
    return parts.join(' ');
  };

  const getStatusBadge = (status: string) => {
    return status === 'present' ? (
      <Badge variant="secondary" className="bg-green-100 text-green-800">
        <CheckCircle className="h-3 w-3 mr-1" />
        Present
      </Badge>
    ) : (
      <Badge variant="destructive">
        <XCircle className="h-3 w-3 mr-1" />
        Absent
      </Badge>
    );
  };

  // Loading States
  if (isAuthLoading) {
    return (
      <SidebarProvider>
        <div className="flex min-h-screen w-full bg-gray-50 dark:bg-gray-900">
          <div className="fixed top-0 left-0 h-screen z-50"><StudentSidebar/></div>
          <div className="flex flex-col flex-1 md:ml-64 lg:ml-64">
            <StudentHeader />
            <main className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Loader2 className="h-10 w-10 animate-spin text-blue-600 mx-auto mb-2" />
                <p className="text-sm text-gray-600">Loading authentication...</p>
              </div>
            </main>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  if (!hasValidClaims || !schoolID) {
    return (
      <SidebarProvider>
        <div className="flex min-h-screen w-full bg-gray-50 dark:bg-gray-900">
          <div className="fixed top-0 left-0 h-screen z-50"><StudentSidebar/></div>
          <div className="flex flex-col flex-1 md:ml-64 lg:ml-64">
            <StudentHeader />
            <main className="p-6">
              <Alert className="border-red-200 bg-red-50">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">Invalid student credentials. Please contact your administrator.</AlertDescription>
              </Alert>
            </main>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  if (!studentData) {
    return (
      <SidebarProvider>
        <div className="flex min-h-screen w-full bg-gray-50 dark:bg-gray-900">
          <div className="fixed top-0 left-0 h-screen z-50"><StudentSidebar/></div>
          <div className="flex flex-col flex-1 md:ml-64 lg:ml-64">
            <StudentHeader />
            <main className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Loader2 className="h-10 w-10 animate-spin text-blue-600 mx-auto mb-2" />
                <p className="text-sm text-gray-600">Loading student profile...</p>
              </div>
            </main>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-gray-50 dark:bg-gray-900">
        {/* Fixed Sidebar */}
        <div className="fixed top-0 left-0 h-screen  z-50">
          <StudentSidebar />
        </div>
        {/* Main Content with offset to prevent overlap */}
        <div className="flex flex-col flex-1 md:ml-64 lg:ml-64">
          <StudentHeader />
          <main className="flex-1 p-4 sm:p-6 overflow-auto bg-gray-50 dark:bg-gray-900">
            <div className="mb-6">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
                My Attendance
              </h1>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                Track your attendance and stay motivated
              </p>
            </div>

            {/* Low Attendance Alert */}
            {attendanceStats.attendancePercentage < 75 && (
              <Alert className="mb-6 border-red-200 bg-red-50">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">
                  <strong>Attention:</strong> Your attendance is below 75%.
                </AlertDescription>
              </Alert>
            )}

            {/* Student Details Card */}
            <Card className="mb-6 bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <User className="h-5 w-5" />
                  Student Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Name</label>
                    <p className="font-semibold">{getFullName(studentData)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Reg Number</label>
                    <p className="font-semibold">{studentData.registrationNumber}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Class</label>
                    <p className="font-semibold">{studentData.class}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Dorm</label>
                    <p className="font-semibold">{dormName ?? studentData.dormName}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Filters */}
            <Card className="mb-6 bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
              <CardContent className="pt-6">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <label className="text-sm font-medium mb-2 block">View Period</label>
                    <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                      <SelectTrigger className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] dark:hover:bg-[#101828] data-[state=selected]:bg-[#111827]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] data-[state=selected]:bg-[#111827]">
                        <SelectItem className="dark:hover:bg-[#101828]" value="month">This Month</SelectItem>
                        <SelectItem className="dark:hover:bg-[#101828]" value="term1">Term 1</SelectItem>
                        <SelectItem className="dark:hover:bg-[#101828]" value="term2">Term 2</SelectItem>
                        <SelectItem className="dark:hover:bg-[#101828]" value="term3">Term 3</SelectItem>
                        <SelectItem className="dark:hover:bg-[#101828]" value="year">Full Year</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <label className="text-sm font-medium mb-2 block">Month</label>
                    <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                      <SelectTrigger className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] dark:hover:bg-[#101828]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                        {Array.from({ length: 12 }, (_, i) => {
                          const month = new Date();
                          month.setMonth(month.getMonth() - i);
                          return (
                            <SelectItem className="dark:hover:bg-[#101828]" key={i} value={format(month, 'yyyy-MM')}>
                              {format(month, 'MMMM yyyy')}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500">My Attendance</p>
                      {loadingAttendance ? (
                        <Loader2 className="h-6 w-6 animate-spin text-blue-600 mt-2" />
                      ) : (
                        <p className={`text-2xl font-bold ${getAttendanceColor(attendanceStats.attendancePercentage)}`}>
                          {attendanceStats.attendancePercentage}%
                        </p>
                      )}
                    </div>
                    <TrendingUp className="h-8 w-8 text-blue-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Class Average</p>
                      {loadingClassStats ? (
                        <Loader2 className="h-6 w-6 animate-spin text-blue-600 mt-2" />
                      ) : (
                        <p className="text-2xl font-bold text-blue-600">{classAverage}%</p>
                      )}
                    </div>
                    <Users className="h-8 w-8 text-blue-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Current Streak</p>
                      {loadingAttendance ? (
                        <Loader2 className="h-6 w-6 animate-spin text-orange-600 mt-2" />
                      ) : (
                        <p className="text-2xl font-bold text-orange-600">{currentStreak} days</p>
                      )}
                    </div>
                    <Flame className="h-8 w-8 text-orange-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Best Streak</p>
                      {loadingAttendance ? (
                        <Loader2 className="h-6 w-6 animate-spin text-purple-600 mt-2" />
                      ) : (
                        <p className="text-2xl font-bold text-purple-600">{maxStreak} days</p>
                      )}
                    </div>
                    <Award className="h-8 w-8 text-purple-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Attendance vs Class Average Chart */}
              <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    My Performance vs Class
                  </CardTitle>
                  <CardDescription>Compare your attendance with class average</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">My Attendance</span>
                      <span className={`font-bold ${getAttendanceColor(attendanceStats.attendancePercentage)}`}>
                        {attendanceStats.attendancePercentage}%
                      </span>
                    </div>
                    <Progress value={attendanceStats.attendancePercentage} className="h-2" />
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Class Average</span>
                      <span className="font-bold text-blue-600">{classAverage}%</span>
                    </div>
                    <Progress value={classAverage} className="h-2" />
                    
                    <div className="text-center pt-4">
                      {attendanceStats.attendancePercentage > classAverage ? (
                        <Badge variant="secondary" className="bg-green-100 text-green-800">
                          Above Class Average! 🎉
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-orange-100 text-orange-800">
                          Room for Improvement 📈
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Class Distribution */}
              <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Class Attendance Distribution
                  </CardTitle>
                  <CardDescription>See how your classmates are performing</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={distribution}>
                        <XAxis 
                          dataKey="range" 
                          tick={{ fontSize: 12 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis 
                          tick={{ fontSize: 12 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip 
                          formatter={(value) => [`${value} students`, 'Count']}
                        />
                        <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Monthly Summary and Recent Records */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Monthly Summary
                  </CardTitle>
                  <CardDescription>Your attendance breakdown for this month</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="p-3 bg-green-50 rounded-lg">
                        <div className="text-xl font-bold text-green-600">{attendanceStats.presentDays}</div>
                        <div className="text-sm text-green-700">Present</div>
                      </div>
                      <div className="p-3 bg-red-50 rounded-lg">
                        <div className="text-xl font-bold text-red-600">{attendanceStats.absentDays}</div>
                        <div className="text-sm text-red-700">Absent</div>
                      </div>
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <div className="text-xl font-bold text-blue-600">{attendanceStats.totalDays}</div>
                        <div className="text-sm text-blue-700">Total Days</div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Attendance Rate</span>
                        <span className={getAttendanceColor(attendanceStats.attendancePercentage)}>
                          {attendanceStats.attendancePercentage}%
                        </span>
                      </div>
                      <Progress value={attendanceStats.attendancePercentage} className="h-2" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Streak Tracker */}
              <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Flame className="h-5 w-5" />
                    Attendance Streaks
                  </CardTitle>
                  <CardDescription>Keep the momentum going!</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="text-center">
                      <div className="text-4xl font-bold text-orange-600 mb-2">{currentStreak}</div>
                      <div className="text-sm text-gray-600">Current Streak (days)</div>
                    </div>
                    
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600 mb-2">{maxStreak}</div>
                      <div className="text-sm text-gray-600">Best Streak (days)</div>
                    </div>

                    <div className="text-center">
                      {currentStreak >= 7 && (
                        <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                          🔥 Week Warrior!
                        </Badge>
                      )}
                      {currentStreak >= 14 && (
                        <Badge variant="secondary" className="bg-orange-100 text-orange-800 ml-2">
                          ⭐ Consistency Champion!
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Daily Attendance Records */}
            <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Recent Daily Records
                </CardTitle>
                <CardDescription>Your attendance records for the selected period</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {attendanceRecords.length ? attendanceRecords.slice().sort((a,b)=> new Date(b.date).getTime()-new Date(a.date).getTime()).map((record, index) => (
                    <div key={`${record.date}-${record.studentId}-${index}`} className="m-2 border-[#E2E8F0] dark:border-[#1E293B] flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800">
                      <div className="flex items-center gap-3">
                        <div className="text-sm font-medium">
                          {format(new Date(record.date), 'EEE, MMM d')}
                        </div>
                        {getStatusBadge(record.status)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {index < currentStreak && record.status === 'present' && (
                          <span className="text-orange-500">🔥</span>
                        )}
                      </div>
                    </div>
                  )) : <div className="text-center py-6 text-gray-500">No attendance records</div>}
                </div>
              </CardContent>
            </Card>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default StudentAttendanceView;